<template>
  <EModal
    :active="active"
    :close-button="false"
    class="personal-counter-modal"
    data-test-id="personal-counter-modal"
    @close="onClose"
  >
    <div class="personal-counter-modal__background">
      <div class="personal-counter-modal__counter" aria-hidden="true">
        <div class="personal-counter-modal__mock">
          <EIcon
            icon="tree-color"
            size="l"
            class="personal-counter-modal__tree"
          />
          20
        </div>
        <EPersonalCounterProgressLinear
          :percentage="0.8"
          class="personal-counter-modal__progress"
        />
        <div class="personal-counter-modal__icon" />
      </div>
    </div>
    <div class="personal-counter-modal__content">
      <EBadge class="personal-counter-modal__badge">
        New
      </EBadge>
      <EHeading as="h3">
        {{ $t('common.header.counter.onboarding.title') }}
      </EHeading>
      <p>
        {{ $t('common.header.counter.onboarding.text') }}
      </p>
      <div class="personal-counter-modal__actions">
        <ELink
          color="dark"
          :href="learnMoreLink"
          target="_blank"
          data-test-id="personal-counter-modal-link"
          @click="onLearnMore"
        >
          {{ $t('common.generic.learnmore') }}
        </ELink>
        <EButton
          variant="solid-green"
          class="personal-counter-modal__confirm"
          data-test-id="personal-counter-modal-confirm"
          @click="onConfirm"
        >
          {{ $t('common.header.counter.onboarding.confirm') }}
        </EButton>
      </div>
    </div>
  </EModal>
</template>

<script>
import { HELPSCOUT_LEARN_MORE_IMPACT_COUNTER } from '~~/common/js/constants/urls';
import { sendCoreAnalyticsEvent, sendEvent } from '~~/common/js/client/analytics';

import EBadge from '~~/common/vue/components/badge';
import EButton from '~~/common/vue/components/button';
import EHeading from '~~/common/vue/components/heading';
import EIcon from '~~/common/vue/components/icon';
import ELink from '~~/common/vue/components/link';
import EModal from '~~/common/vue/components/modal';
import EPersonalCounterProgressLinear from './progress-linear';

export default {
  name: 'EPersonalCounterModal',
  components: {
    EBadge,
    EButton,
    EHeading,
    EIcon,
    ELink,
    EModal,
    EPersonalCounterProgressLinear,
  },
  props: {
    active: {
      type: Boolean,
      required: true,
    },
  },
  computed: {
    learnMoreLink() {
      return HELPSCOUT_LEARN_MORE_IMPACT_COUNTER;
    },
  },
  methods: {
    onClose() {
      sendCoreAnalyticsEvent('personalCounterModalClose');
      sendEvent('personal-counter-modal-close');
      this.$emit('close');
    },
    onConfirm() {
      sendCoreAnalyticsEvent('personalCounterModalConfirm');
      sendEvent('personal-counter-modal-confirm');
      this.$emit('close');
    },
    onLearnMore() {
      sendCoreAnalyticsEvent('personalCounterModalLearnMore');
      sendEvent('personal-counter-modal-learn-more');
    },
  },
};
</script>

<style lang="scss" scoped>
.personal-counter-modal ::v-deep .modal__dialog {
  align-items: flex-start;
  border-radius: $border-radius-l;
  overflow: hidden;
  padding: 0;
  position: relative;
}

.personal-counter-modal__background {
  background-image: url('~~/common/vue/assets/images/personal-counter-modal-trees.svg?asPath');
  background-repeat: no-repeat;
  background-size: cover;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  height: 200px;
  width: 100%;

  @include tablet {
    height: 295px;
  }
}

.personal-counter-modal__background::after {
  background-color: var(--color-background-primary);
  content: '';
  display: block;
  mask-image: url('~~/common/vue/assets/images/personal-counter-modal-wave.svg?asPath');
  mask-repeat: no-repeat;
  mask-size: cover;
  position: absolute;
  bottom: -1px;
  height: 55px;
  width: 100%;

  @include tablet {
    height: 85px;
  }
}

.personal-counter-modal__counter {
  background-color: var(--color-button-background-secondary);
  border-radius: $border-radius-2l;
  font-size: $font-3l;
  font-weight: $font-weight-l;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  padding: $space-s $space-l $space-1s;
  position: relative;
  height: 70px;
  width: 137px;
}

.personal-counter-modal__mock {
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: $space-s;
}

.personal-counter-modal__tree {
  color: var(--color-brand-primary);
}

.personal-counter-modal__progress {
  transform: scaleY(2);
  width: 90%;
}

.personal-counter-modal__icon {
  position: absolute;
  bottom: -$space-2s;
  width: 28px;
  height: 28px;
  background-image: url('~~/common/vue/assets/images/plus-one.svg?asPath');
}

.personal-counter-modal__content {
  padding: $space-m $space-m $space-3l;

  @include tablet {
    padding: 0 $space-1l $space-3l;
  }
}

.personal-counter-modal__badge {
  margin-bottom: $space-1s;
}

.personal-counter-modal__actions {
  display: flex;
  flex-direction: column-reverse;
  align-items: center;
  gap: $space-s $space-l;
  margin-top: $space-1l;

  @include tablet {
    margin-top: $space-2l;
    flex-direction: row;
    justify-content: flex-end;
  }
}

.personal-counter-modal__confirm {
  align-self: stretch;
}

.dark .personal-counter-modal__background {
  background-image: url(
    '~~/common/vue/assets/images/personal-counter-modal-trees-dark.svg?asPath'
  );

  .personal-counter-modal__icon {
    background-image: url('~~/common/vue/assets/images/plus-one-dark.svg?asPath');
  }
}
</style>
